// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// Copyright 2026 Amantus Machina GmbH

import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { isNonEmptyArray } from '@/lib/utils/array-helpers';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

const FILE_EXTENSION_PATTERN = /\.[a-zA-Z0-9]+$/;
const isMediaType = (value: string): value is MediaItem['type'] =>
  value === 'photo' || value === 'video' || value === 'animated_gif';

/**
 * Twitter Media URL Construction Utilities
 *
 * Data-flow contract (2025):
 * - The database stores only values returned by Twitter. We never persist
 *   fabricated CDN URLs (like pbs.twimg.com) derived from media_key.
 * - Any best-effort synthesis happens only at the UI/read layer so we can
 *   adapt quickly if formats change without migrating data.
 *
 * Note on media_key: "{type}_{id}" is NOT a stable CDN identifier for photos.
 * Generating pbs URLs from it produces the yellow warning graphic on X.
 */

export interface MediaItem {
  alt_text?: string;
  duration_ms?: number;
  height?: number;
  media_key: string;
  preview_image_url?: string;
  type: 'photo' | 'video' | 'animated_gif';
  url?: string;
  variants?: Array<{
    bit_rate?: number;
    content_type: string;
    url: string;
  }>;
  width?: number;
}

// Type for processed media items with guaranteed URLs
export type ProcessedMediaItem = MediaItem & {
  url: string;
  preview_image_url: string;
  width: number;
  height: number;
};

type RawMediaRecord = Record<string, unknown> & {
  type?: unknown;
  media_key?: unknown;
  url?: unknown;
  preview_image_url?: unknown;
  width?: unknown;
  height?: unknown;
  alt_text?: unknown;
  duration_ms?: unknown;
  variants?: unknown;
};

export type PhotoSize = 'thumb' | 'small' | 'medium' | 'large' | 'orig';

/**
 * Ensure a Twitter photo URL carries the desired `name` size parameter, without
 * fabricating the CDN identifier. The input must be a real URL returned by the
 * API. This is safe for display‑time sizing only.
 */
export function withPhotoSize(url: string, size: PhotoSize = 'large'): string {
  try {
    const u = new URL(url);
    // Twitter supports name=[thumb|small|medium|large|orig]
    u.searchParams.set('name', size);
    // Add a default format for twimg photos if none is provided and pathname lacks extension
    try {
      const host = u.hostname.toLowerCase();
      const hasFormat = u.searchParams.has('format');
      const hasExtension = FILE_EXTENSION_PATTERN.test(u.pathname);
      const isTwitterCdnHost = host.endsWith('pbs.twimg.com') || host.endsWith('video.twimg.com');
      if (!(hasFormat || hasExtension) && isTwitterCdnHost) {
        u.searchParams.set('format', 'jpg');
      }
    } catch {
      // ignore format inference errors
    }
    return u.toString();
  } catch {
    return url; // fall back to original
  }
}

/**
 * Get the display URL for a photo MediaItem respecting the requested size.
 * Returns undefined for non-photos or when no hard URL is present.
 */
export function getPhotoUrl(media: MediaItem, size: PhotoSize = 'large'): string | undefined {
  if (!media || media.type !== 'photo' || !media.url) return;
  return withPhotoSize(media.url, size);
}

/**
 * Pick the highest-bitrate MP4 variant for videos/GIFs. Falls back to the
 * first available variant when MP4s are not present.
 */
export function pickBestVideoVariant(media: MediaItem): string | undefined {
  if (!media || (media.type !== 'video' && media.type !== 'animated_gif')) return;
  if (!(Array.isArray(media.variants) && isNonEmptyArray(media.variants))) return;
  const mp4 = toSortedCompat(
    media.variants.filter((v) => v.content_type === 'video/mp4' && typeof v.url === 'string'),
    (a, b) => (b.bit_rate || 0) - (a.bit_rate || 0)
  );
  if (isNonEmptyArray(mp4)) return mp4[0]?.url;
  const any = media.variants.find((v) => typeof v.url === 'string');
  return any?.url;
}

/**
 * Constructs a Twitter media URL from a media_key
 *
 * @param mediaKey - The media_key from Twitter API (e.g., "31_959_354_712_764_878_849")
 * @param type - The media type (photo, video, animated_gif)
 * @param options - Optional parameters for URL construction
 * @returns The constructed media URL or empty string if unable to construct
 */
// UI-only helper; never used on write-paths.
export function constructMediaUrl(mediaKey: string, type: 'photo' | 'video' | 'animated_gif'): string {
  if (!mediaKey) {
    return '';
  }

  // Extract the media ID from the key (remove type prefix)
  const mediaId = mediaKey.split('_')[1];
  if (mediaId === undefined) {
    return '';
  }

  // IMPORTANT: We cannot reliably construct a photo URL from media_key.
  // The CDN uses hashed identifiers, not the numeric part of media_key.
  // Returning a fabricated URL causes broken images and X's yellow warning graphic.
  if (type === 'photo') {
    return '';
  }

  // For videos and animated_gifs, provide a preview image. The playable URL
  // must come from the `variants` array returned by Twitter.
  if (type === 'video' || type === 'animated_gif') {
    // Return preview image URL for videos
    return `https://pbs.twimg.com/media/${mediaId}?format=jpg&name=large`;
  }

  return '';
}

/**
 * Constructs a preview/thumbnail URL for any media type
 *
 * @param mediaKey - The media_key from Twitter API
 * @returns The preview image URL
 */
// UI-only convenience: small preview image. Safe to change without DB churn.
export function constructPreviewUrl(mediaKey: string): string {
  if (!mediaKey) {
    return '';
  }

  const mediaId = mediaKey.split('_')[1];
  if (mediaId === undefined) {
    return '';
  }

  return `https://pbs.twimg.com/media/${mediaId}?format=jpg&name=small`;
}

/**
 * Enhances media items with constructed URLs if they're missing
 *
 * @param mediaItems - Array of media items from Twitter API
 * @returns Media items with URLs
 */
function resolvePreviewUrl(media: MediaItem): string {
  const preview =
    typeof media.preview_image_url === 'string' && media.preview_image_url.length > 0
      ? media.preview_image_url
      : undefined;
  if (preview) {
    return preview;
  }
  return '';
}

function resolvePhotoUrl(media: MediaItem): string {
  const direct = typeof media.url === 'string' && media.url.length > 0 ? media.url : undefined;
  if (direct) {
    return direct;
  }
  const preview = resolvePreviewUrl(media);
  if (preview) {
    return preview;
  }
  return '';
}

export function enhanceMediaItems(mediaItems: MediaItem[]): ProcessedMediaItem[] {
  return mediaItems.map((item) => {
    // Construct URLs based on media type
    const url =
      item.type === 'photo' ? resolvePhotoUrl(item) : item.url || constructMediaUrl(item.media_key, item.type);
    const preview_image_url = resolvePreviewUrl(item);

    return {
      ...item,
      url,
      preview_image_url,
      // Set default dimensions if not provided
      width: item.width || (item.type === 'photo' ? 1200 : 1280),
      height: item.height || (item.type === 'photo' ? 675 : 720),
    } as ProcessedMediaItem;
  });
}

/**
 * Processes media data from Twitter API response
 * Handles both cases where URLs are provided and where they need to be constructed
 *
 * @param media - Media array from Twitter API includes
 * @returns Processed media items with URLs
 */
export function processTwitterMedia(media: Record<string, unknown>[]): ProcessedMediaItem[] {
  return media.map((item) => {
    const record: RawMediaRecord = item;
    const rawType = typeof record.type === 'string' ? record.type : 'photo';
    const normalizedType = isMediaType(rawType) ? rawType : 'photo';

    const mediaKey = typeof record.media_key === 'string' ? record.media_key : '';

    const mediaItem: MediaItem = {
      media_key: mediaKey,
      type: normalizedType,
    };
    const urlValue = record.url;
    if (typeof urlValue === 'string') {
      mediaItem.url = urlValue;
    }
    const previewValue = record.preview_image_url;
    if (typeof previewValue === 'string') {
      mediaItem.preview_image_url = previewValue;
    }
    const widthValue = record.width;
    if (typeof widthValue === 'number') {
      mediaItem.width = widthValue;
    }
    const heightValue = record.height;
    if (typeof heightValue === 'number') {
      mediaItem.height = heightValue;
    }
    const altValue = record.alt_text;
    if (typeof altValue === 'string') {
      mediaItem.alt_text = altValue;
    }
    const durationValue = record.duration_ms;
    if (typeof durationValue === 'number') {
      mediaItem.duration_ms = durationValue;
    }
    const variantsValue = record.variants;
    if (Array.isArray(variantsValue) && variantsValue.length > 0) {
      const variants = variantsValue.filter((variant): variant is NonNullable<MediaItem['variants']>[number] => {
        if (!isRecord(variant)) {
          return false;
        }
        if (typeof getRecordValue(variant, 'content_type') !== 'string') {
          return false;
        }
        if (typeof getRecordValue(variant, 'url') !== 'string') {
          return false;
        }
        const bitRate = getRecordValue(variant, 'bit_rate');
        return bitRate === undefined || typeof bitRate === 'number';
      });
      if (variants.length > 0) {
        mediaItem.variants = variants;
      }
    }

    const resolvedUrl =
      mediaItem.type === 'photo'
        ? resolvePhotoUrl(mediaItem)
        : (mediaItem.url ?? constructMediaUrl(mediaItem.media_key, mediaItem.type));
    const previewImageUrl = resolvePreviewUrl(mediaItem);

    let width = mediaItem.width;
    if (width === undefined) {
      width = mediaItem.type === 'photo' ? 1200 : 1280;
    }
    let height = mediaItem.height;
    if (height === undefined) {
      height = mediaItem.type === 'photo' ? 675 : 720;
    }

    const processed: ProcessedMediaItem = {
      media_key: mediaKey,
      type: mediaItem.type,
      url: resolvedUrl ?? '',
      preview_image_url: previewImageUrl ?? '',
      width,
      height,
    };

    if (mediaItem.alt_text) {
      processed.alt_text = mediaItem.alt_text;
    }
    if (mediaItem.duration_ms) {
      processed.duration_ms = mediaItem.duration_ms;
    }
    if (Array.isArray(mediaItem.variants)) {
      processed.variants = mediaItem.variants;
    }

    return processed;
  });
}
